import React, {
    FC,
    useCallback,
    useMemo,
    Dispatch,
    useState,
    useEffect,
    SetStateAction
} from "react"
import { Map } from "immutable"
import { turbine } from "neql"
import { Loader } from "dash-styled"

import {
    FetchState,
    getValue,
    hasValue
} from "../../../../../../../../../../../Models/Client/FetchState"
import { UserPlan } from "../../../../../../../../../../../Models/API/UserModel"
import { Result } from "../../../../../../../../../../../Models/Utility/Result"
import { useUserPlan } from "../../../../../../../../../../../Hooks/useUserPlan"
import { useAppInstance } from "../../../../../../../../../../../Hooks/useAppInstance"
import { useAppTemplate } from "../../../../../../../../../../../Hooks/useAppTemplate"
import { useAppPlansMetadata } from "../../../../../../../../../../../Hooks/useAppPlansMetadata"
import { useMetricVariants } from "../../../../../../../../../../../Hooks/useMetricVariants"
import { useMetricSelection } from "../../../../../../../../../../../Hooks/useMetricSelection"
import { useAppMeasurerConfiguration } from "../../../../../../../../../../../Hooks/useAppMeasurerConfiguration"
import { useAppMeasurerMetrics } from "../../../../../../../../../../../Hooks/useAppMeasurerMetrics"
import { useNeqlContextProvider } from "../../../../../../../../../../../Hooks/useNeqlContextProvider"
import { useAppState } from "../../../../../../../../../../../Hooks/useReduxState"
import { useMetricConstraints } from "../../../../../../../../../../../Hooks/useMetricConstraints"
import { useMeasurerMetricList } from "../../../../../../../../../../../Hooks/useMeasurerMetricList"
import { selectIsSysAdmin } from "../../../../../../../../../../../Helpers/Functions/Selectors/auth"
import { SettingsSection } from "../../../../../../Common/SettingsSection"
import { SettingsSubSection } from "../../../../../../Common/SettingsSubSection"

import {
    FormFields,
    FormFieldsWhoShouldLabel,
    FormFieldsLabelerRestriction,
    getEmailValidationErrors,
    createInitialValue,
    TaskName,
    restrictionToFormValues
} from "./FormFields"
import { MeasurerMetric } from "./Measurement/MeasurerMetric"
import { WhoShouldLabel } from "./WhoShouldLabel/WhoShouldLabel"
import { LabelerPoolRestrictions } from "./LabelerPoolRestrictions/LabelerPoolRestrictions"
import { Measurement } from "./Measurement/Measurement"
import { LabelerPayments } from "./LabelerPayments/LabelerPayments"
import { AppSettingsPlan } from "./Plan/Plan"

type HITLSectionProps = {
    appID: string
    value?: FormFields
    taskNamesFetch: Result<string[]>
    appParams?: any
    onShowUserExpiryContact: () => void
    onChange: Dispatch<SetStateAction<FormFields | undefined>>
    setChanged: (state: boolean) => void
    setEmailErrors: (state: Map<TaskName, string>) => void
}

/**
 * Human in the Loop settings section
 */
export const HITLSection: FC<HITLSectionProps> = ({
    appID,
    taskNamesFetch,
    value,
    appParams,
    onShowUserExpiryContact,
    onChange,
    setChanged,
    setEmailErrors
}) => {
    const [qualificationMetricNameSearch, setQualificationMetricNameSearch] =
        useState<string | undefined>(undefined)

    const instanceFetch = useAppInstance(appID)

    const templateFetch = useAppTemplate(appID)

    const plansMetadataFetch = useAppPlansMetadata(appID)
    const metricVariantsFetch = useMetricVariants(appID)
    const { qualityName } = useMetricSelection(appID)
    const measurerConfigurationFetch = useAppMeasurerConfiguration(appID)
    const appMeasurerMetrics = useAppMeasurerMetrics(appID)

    const clientContext = useNeqlContextProvider("turbine")

    const isSysAdmin = useAppState(selectIsSysAdmin)

    const [qualificationMetricNamesFetch, loadMoreQualificationMetricNames] =
        useMetricConstraints(qualificationMetricNameSearch)

    const fetchTrainingTests = turbine.useFetchTrainingTestsQuery({
        variables: {
            trainingTestsAppUuid: appID
        },
        client: clientContext
    })
    const trainingTests: turbine.TrainingTest[] = useMemo(
        () =>
            (fetchTrainingTests.data?.trainingTests ?? []).filter(
                trainingTest =>
                    trainingTest !== undefined && trainingTest !== null
            ) as turbine.TrainingTest[],
        [fetchTrainingTests.data]
    )

    const formFieldsFetch = useMemo<FetchState<FormFields>>(
        () =>
            !hasValue(plansMetadataFetch)
                ? plansMetadataFetch
                : !hasValue(instanceFetch)
                ? instanceFetch
                : !hasValue(templateFetch)
                ? templateFetch
                : !hasValue(taskNamesFetch)
                ? taskNamesFetch
                : Result.ok(
                      createInitialValue(
                          taskNamesFetch.value,
                          plansMetadataFetch.value,
                          instanceFetch.value,
                          templateFetch.value,
                          getValue(measurerConfigurationFetch),
                          getValue(appMeasurerMetrics)
                      )
                  ),
        [
            plansMetadataFetch,
            instanceFetch,
            templateFetch,
            taskNamesFetch,
            measurerConfigurationFetch,
            appMeasurerMetrics
        ]
    )

    const defaultLabelerPoolRestrictionsFetch = useMemo<
        FetchState<FormFieldsLabelerRestriction[]>
    >(
        () =>
            !hasValue(templateFetch)
                ? templateFetch
                : !hasValue(instanceFetch)
                ? instanceFetch
                : !hasValue(taskNamesFetch)
                ? taskNamesFetch
                : Result.ok(
                      restrictionToFormValues(
                          taskNamesFetch.value,
                          templateFetch.value.defaultConstraints
                      )
                  ),
        [templateFetch, instanceFetch, taskNamesFetch]
    )

    const HITLSettingsChanged =
        hasValue(formFieldsFetch) &&
        value !== undefined &&
        JSON.stringify(value) !== JSON.stringify(formFieldsFetch.value)

    const emailErrors = useMemo(
        () => getEmailValidationErrors(value?.labelerRestrictions || []),
        [value]
    )

    useEffect(() => {
        onChange(hasValue(formFieldsFetch) ? formFieldsFetch?.value : undefined)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formFieldsFetch])

    useEffect(() => {
        setEmailErrors(emailErrors)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [emailErrors])

    useEffect(() => {
        setChanged(HITLSettingsChanged)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [HITLSettingsChanged])

    const userPlan = useUserPlan()
    const measurerMetricsFetch = useMeasurerMetricList(appID)

    const handleChange = useCallback(
        (value: Record<string, FormFields[keyof FormFields]>) => {
            if (
                value.whoShouldLabel &&
                (value.whoShouldLabel as FormFieldsWhoShouldLabel)
                    .isCanoticCrowd &&
                userPlan !== UserPlan.FULL
            ) {
                return onShowUserExpiryContact()
            }
            onChange(
                f =>
                    ({
                        ...(f || {}),
                        ...value
                    } as FormFields)
            )
        },
        [userPlan, onChange, onShowUserExpiryContact]
    )

    const shouldShowPlans =
        value?.whoShouldLabel?.isCanoticCrowd &&
        (isSysAdmin ||
            (value.whoShouldLabel.isCanoticCrowd &&
                value.plan.workflowConfigs.some(
                    config => !config.hiddenFromCustomer
                )))

    const shouldShowWhoShouldLabel = value?.whoShouldLabel?.isVisible
    const shouldShowLabelerPoolRestrictions =
        isSysAdmin &&
        value?.whoShouldLabel?.isCanoticCrowd &&
        shouldShowWhoShouldLabel
    const shouldShowLabelerPayments =
        isSysAdmin &&
        value?.whoShouldLabel?.isCanoticCrowd &&
        shouldShowWhoShouldLabel
    const shouldShowMeasurement =
        isSysAdmin &&
        value?.whoShouldLabel?.isCanoticCrowd &&
        shouldShowWhoShouldLabel

    if (
        !hasValue(taskNamesFetch) ||
        !hasValue(plansMetadataFetch) ||
        !hasValue(defaultLabelerPoolRestrictionsFetch)
    ) {
        return <Loader />
    }

    return (
        <SettingsSection title="Human in the Loop settings" titleComponent="h3">
            {shouldShowPlans && (
                <SettingsSubSection
                    title="Plan"
                    titleComponent="h4"
                    testId="plan-settings"
                >
                    <AppSettingsPlan
                        isSysAdmin={isSysAdmin}
                        plans={plansMetadataFetch.value}
                        metricsFetch={metricVariantsFetch}
                        qualityName={qualityName}
                        value={value.plan}
                        onChange={handleChange}
                    />
                </SettingsSubSection>
            )}
            {shouldShowWhoShouldLabel && (
                <SettingsSubSection
                    title="Who should process"
                    titleComponent="h4"
                    testId="crowd-switch-settings"
                >
                    <WhoShouldLabel
                        isSysAdmin={isSysAdmin}
                        value={value.whoShouldLabel}
                        onChange={handleChange}
                    />
                </SettingsSubSection>
            )}
            {shouldShowLabelerPoolRestrictions && (
                <SettingsSubSection
                    title="Worker pool restrictions"
                    titleComponent="h4"
                    testId="labeler-restriction-settings"
                >
                    <LabelerPoolRestrictions
                        taskNames={taskNamesFetch.value}
                        qualificationMetricNamesFetch={
                            qualificationMetricNamesFetch
                        }
                        emailErrors={emailErrors}
                        value={value.labelerRestrictions}
                        defaultValue={defaultLabelerPoolRestrictionsFetch.value}
                        onChange={handleChange}
                        onSearchQualificationMetricNames={
                            setQualificationMetricNameSearch
                        }
                        onLoadMoreQualificationMetricNames={
                            loadMoreQualificationMetricNames
                        }
                        trainingTests={trainingTests}
                    />
                </SettingsSubSection>
            )}
            {shouldShowLabelerPayments && (
                <SettingsSubSection
                    title="Worker payments"
                    titleComponent="h4"
                    testId="payment-settings"
                >
                    <LabelerPayments
                        taskNames={taskNamesFetch.value}
                        value={value.labelerPayments}
                        onChange={handleChange}
                    />
                </SettingsSubSection>
            )}
            {shouldShowMeasurement && value.measurement !== undefined && (
                <>
                    <SettingsSubSection
                        title="Measurement"
                        titleComponent="h4"
                        testId="measurement-settings"
                    >
                        <Measurement
                            value={value.measurement}
                            onChange={handleChange}
                        />
                    </SettingsSubSection>
                    {hasValue(measurerMetricsFetch) &&
                        value.measurement.scope !== "off" && (
                            <SettingsSubSection
                                title="Measurer metrics"
                                titleComponent="h4"
                                testId="measurer-metric-settings"
                            >
                                <MeasurerMetric
                                    appMeasurerMetrics={value.measurerMetrics}
                                    measurerMetrics={measurerMetricsFetch}
                                    onChange={handleChange}
                                    appParams={appParams}
                                />
                            </SettingsSubSection>
                        )}
                </>
            )}
        </SettingsSection>
    )
}
